import { lt, useFeatureFlag } from '@mews/framework';
import { DataTestId, FeatureFlag } from '@mews-commander/enums';
import { useDialogForm } from '@mews-commander/form';
import { OptimusProvider } from '@mews-commander/ui';
import {
    AccountingConfigurationOptions,
    AccountingState,
    AuthorizationType,
    InvoicePaymentType,
    PaymentTableRecord,
    PaymentTableRecordPaymentActions,
    PaymentType,
} from '@mews-commander/data-access';
import { IconName } from '@optimus-web/icons';
import { compactArray } from '@mews/fp';
import { useCancelAuthorization, useCancelTerminalRequest, useResendAuthorizationEmail } from '../../../queries';
import { PaymentLinkReservationDialogContent } from '../../forms/PaymentLink/PaymentLinkReservationDialogContent';
import { PaymentLinkInvoiceDialogContent } from '../../forms/PaymentLink/PaymentLinkInvoiceDialogFormContent';
import { PaymentsTablesActionsMenu } from '../PaymentsTablesActionsMenu';
import { useRefundPaymentDialog } from './hooks';

const UNLINKABLE_PAYMENT_METHODS = [PaymentType.Ghost, PaymentType.TaxDeducted];

interface Props {
    paymentActions: PaymentTableRecordPaymentActions[];
    paymentId: string;
    serviceOrderId?: string | null;
    paymentAccountingState?: AccountingState | null;
    ownerId: string;
    paymentData: PaymentTableRecord['data'];
    refetchTableData: () => void;
    receivableTrackingEnabled: AccountingConfigurationOptions['receivableTrackingEnabled'];
    isCompany?: boolean;
}

export const PaymentsActionsMenu = ({
    paymentActions,
    paymentId,
    serviceOrderId,
    ownerId,
    paymentAccountingState,
    paymentData,
    refetchTableData,
    receivableTrackingEnabled,
    isCompany = false,
}: Props) => {
    const resendAuthorizationMail = useResendAuthorizationEmail();
    const cancelAuthorization = useCancelAuthorization();
    const cancelTerminalRequest = useCancelTerminalRequest();
    const [createRefundPaymentDialog, toggleCreateRefundPaymentDialog] = useRefundPaymentDialog({
        paymentId,
        accountId: ownerId,
    });

    const { discriminator: paymentType } = paymentData;
    const reservationLinkToPaymentsEnabled = useFeatureFlag(FeatureFlag.reservationLinkToPaymentsEnabled);

    const [renderChangeReservationLinkDialog, toggleChangeReservationLinkDialog] = useDialogForm({
        title: serviceOrderId ? lt('ChangeReservationLink') : lt('CreatePaymentPlanFormReservationLabel'),
        renderForm: (props) => (
            <PaymentLinkReservationDialogContent
                {...props}
                paymentId={paymentId}
                accountId={ownerId}
                serviceOrderId={serviceOrderId}
                onSuccess={() => {
                    refetchTableData();
                    toggleChangeReservationLinkDialog();
                }}
            />
        ),
    });

    const [renderLinkPaymentToInvoiceDialog, toggleLinkPaymentToInvoiceDialog] = useDialogForm({
        title: lt('SelectInvoicesToLinkPaymentLabel'),
        renderForm: (props) => (
            <PaymentLinkInvoiceDialogContent
                {...props}
                paymentId={paymentId}
                accountId={ownerId}
                onSuccess={() => {
                    refetchTableData();
                    toggleLinkPaymentToInvoiceDialog();
                }}
            />
        ),
    });

    const reservationLinkAction =
        reservationLinkToPaymentsEnabled &&
        !isCompany &&
        (!UNLINKABLE_PAYMENT_METHODS.includes(paymentType) ||
            (paymentType === PaymentType.Invoice && paymentData.value.type === InvoicePaymentType.Overpayment))
            ? {
                  key: 'LinkReservations',
                  icon: IconName.Reservations,
                  text: serviceOrderId ? lt('ChangeReservationLink') : lt('PaymentsActionsLinkToReservation'),
                  onClick: toggleChangeReservationLinkDialog,
              }
            : null;

    const invoiceLinkAction =
        receivableTrackingEnabled &&
        (!UNLINKABLE_PAYMENT_METHODS.includes(paymentType) ||
            (paymentType === PaymentType.Invoice && paymentData.value.type === InvoicePaymentType.Overpayment)) &&
        paymentAccountingState === AccountingState.Open
            ? {
                  key: 'LinkInvoice',
                  icon: IconName.PaymentReport,
                  text: lt('PaymentsActionsLinkToInvoice'),
                  onClick: toggleLinkPaymentToInvoiceDialog,
              }
            : null;

    const linkingActions = [reservationLinkAction, invoiceLinkAction];
    const compactLinkingActions = compactArray(linkingActions);

    const actions = [
        {
            key: PaymentTableRecordPaymentActions.Refund,
            icon: IconName.Preauthorization,
            text: lt('Refund'),
            onClick: toggleCreateRefundPaymentDialog,
            dataTestId: DataTestId.RefundPaymentActionButton,
        },

        {
            key: PaymentTableRecordPaymentActions.ResendAuthorizationMail,
            icon: IconName.Mails,
            text: lt('ResendEmail'),
            onClick: () =>
                resendAuthorizationMail.mutate({
                    id: paymentId,
                    authorizationType: AuthorizationType.CreditCardPayment,
                }),
        },
        {
            key: PaymentTableRecordPaymentActions.CancelAuthorization,
            icon: IconName.Disable,
            text: lt('Cancel'),
            onClick: () =>
                cancelAuthorization.mutate(
                    {
                        id: paymentId,
                        authorizationType: AuthorizationType.CreditCardPayment,
                    },
                    {
                        onSuccess: refetchTableData,
                    }
                ),
        },
        {
            key: PaymentTableRecordPaymentActions.CancelTerminalRequest,
            icon: IconName.Disable,
            text: lt('Cancel'),
            onClick: () =>
                cancelTerminalRequest.mutate(
                    {
                        id: paymentId,
                        authorizationType: AuthorizationType.CreditCardPayment,
                    },
                    {
                        onSuccess: refetchTableData,
                    }
                ),
        },
    ];

    const allActions = [...compactLinkingActions, ...actions.filter((action) => paymentActions.includes(action.key))];

    if (!allActions.length) {
        return null;
    }

    return (
        <>
            {allActions.length > 0 ? (
                <>
                    <PaymentsTablesActionsMenu actions={allActions} />
                    <OptimusProvider density="spacious">
                        {createRefundPaymentDialog()}
                        {renderChangeReservationLinkDialog()}
                        {renderLinkPaymentToInvoiceDialog()}
                    </OptimusProvider>
                </>
            ) : null}
        </>
    );
};
